import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Lock,
  Mail,
  Phone,
  FileText,
  UserCheck,
  Building2,
} from "lucide-react";
const PrivacyPolicy = () => {
  const sections = [
    {
      icon: <UserCheck size={22} />,
      title: "Information We Collect",
      text: "We may collect basic details such as name, phone number, email address, course interest, and message submitted through the enquiry or contact form.",
    },
    {
      icon: <FileText size={22} />,
      title: "How We Use Your Information",
      text: "The information submitted by users is used only for admission enquiry, course guidance, communication, and college-related support.",
    },
    {
      icon: <Lock size={22} />,
      title: "Data Protection",
      text: "We respect your privacy and take reasonable steps to protect your personal information from unauthorized access or misuse.",
    },
    {
      icon: <ShieldCheck size={22} />,
      title: "Information Sharing",
      text: "We do not sell, rent, or share your personal information with any third party, except when required by law or for official college communication.",
    },
  ];
  return (
    <section className="w-full bg-[#F6F8FC] py-12 md:py-16 overflow-hidden">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10"
          >
            <span className="inline-flex items-center gap-2 bg-[#071A52] text-[#FFD700] px-5 py-2 rounded-full text-xs font-black uppercase tracking-wide mb-4 shadow">
              <ShieldCheck size={15} />
              Privacy Policy
            </span>
            <h1 className="text-3xl md:text-5xl font-black text-[#071A52] leading-tight">
              Privacy Policy
            </h1>
            <div className="w-24 h-1.5 bg-[#FFD700] rounded-full mx-auto mt-4 mb-4"></div>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              This Privacy Policy explains how Pt. Shrinivas Dwivedi
              Mahavidyalaya collects, uses, and protects information submitted
              through this website.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 35 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
            className="bg-white rounded-[30px] shadow-xl border border-gray-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-[#071A52] via-[#0B1F5C] to-[#102B75] p-6 md:p-8 text-white relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-52 h-52 bg-[#FFD700]/20 rounded-full blur-3xl"></div>

              <div className="relative z-10 flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#FFD700] text-[#071A52] flex items-center justify-center shrink-0">
                  <Building2 size={28} />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-black">
                    Pt. Shrinivas Dwivedi Mahavidyalaya
                  </h2>
                  <p className="text-white/75 text-sm mt-2">
                    Arangi, Chandauli, Uttar Pradesh
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-5 mb-8">
                {sections.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: index * 0.08 }}
                    viewport={{ once: true }}
                    className="bg-[#F6F8FC] border border-gray-100 rounded-2xl p-5 hover:shadow-md transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#071A52] text-[#FFD700] flex items-center justify-center mb-4">
                      {item.icon}
                    </div>
                    <h3 className="text-lg font-black text-[#071A52] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {item.text}
                    </p>
                  </motion.div>
                ))}
              </div>
              <div className="space-y-6 text-gray-700 text-sm md:text-base leading-relaxed">
                <div>
                  <h3 className="text-xl font-black text-[#071A52] mb-2">
                    Use of Website
                  </h3>
                  <p>
                    Users are requested to provide accurate information while
                    submitting any enquiry form. The website content is intended
                    for general information about the college, courses,
                    facilities, and contact details.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-black text-[#071A52] mb-2">
                    Cookies & Tracking
                  </h3>
                  <p>
                    This website may use basic cookies or analytics tools to
                    improve user experience and understand website performance.
                    No sensitive personal data is collected without user
                    consent.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-black text-[#071A52] mb-2">
                    Third-Party Links
                  </h3>
                  <p>
                    The website may include links such as Google Maps, WhatsApp,
                    or email links. We are not responsible for the privacy
                    practices of third-party websites or services.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-black text-[#071A52] mb-2">
                    Policy Updates
                  </h3>
                  <p>
                    The college may update this Privacy Policy from time to time.
                    Any changes will be reflected on this page.
                  </p>
                </div>
              </div>
              <div className="mt-8 bg-[#071A52] rounded-2xl p-5 md:p-6 text-white">
                <h3 className="text-xl font-black text-[#FFD700] mb-4">
                  Contact for Privacy Related Queries
                </h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <p className="flex gap-3">
                    <Mail size={18} className="text-[#FFD700] shrink-0" />
                    <span>psndwivedi@gmail.com</span>
                  </p>
                  <p className="flex gap-3">
                    <Phone size={18} className="text-[#FFD700] shrink-0" />
                    <span>+91-9451887217, +91-9450181867</span>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
export default PrivacyPolicy;